<template>
  <div>
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      />
    </v-overlay>
    <UniqueEmailDialog
      :showDialog="showNonUniqueEmail"
      :email="primary.email"
      :onCheck="checkEmail"
      :onClose="() => showNonUniqueEmail = false"
    />
    <div class="form-container">
      <div v-if="!isJunior" class="form-label">Primary Member</div>
      <v-form ref="primaryForm" v-model="isPrimaryValid">
        <v-text-field
          v-if="!!primary.memberId"
          outlined
          v-model="primary.memberId"
          label="Member ID"
          :disabled="true"
        />
        <v-text-field
          outlined
          v-model="primary.firstName"
          label="First Name"
          :disabled="!makeChanges"
          :rules="[(v) => !!v || 'First name is required']"
        />
        <v-text-field
          outlined
          v-model="primary.lastName"
          label="Last Name"
          :disabled="!makeChanges"
          :rules="[(v) => !!v || 'Last name is required']"
        />
        <v-text-field
          outlined
          v-model="primary.email"
          label="Email"
          :disabled="!makeChanges"
          :rules="[validateEmail]"
        />
        <InternationalPhoneNumberInput
          required
          :phone="primary.phone"
          :disabled="!makeChanges"
          @changeData="(v) => primary.phone = v.number.e164"
        />
        <CountryDropdown
          :disabled="!makeChanges"
          :selectedCountry="primary.country"
          v-on:update="updateCountry"
        />
        <StateDropdown
          v-if="primary.country === country_USA"
          required
          :disabled="!makeChanges"
          :selectedState="primary.stateProvince"
          v-on:update="primary.stateProvince = $event"
        />
        <v-row>
          <v-col
            :lg="primary.country && primary.country !== country_USA ? 8 : 12"
            :md="primary.country && primary.country !== country_USA ? 8 : 12"
            :sm="primary.country && primary.country !== country_USA ? 6 : 12"
          >
            <v-text-field
              outlined
              v-model="primary.address"
              label="Address"
              :disabled="!makeChanges"
              :rules="[(v) => !!v || 'Address is required']"
            />
          </v-col>
          <v-col
            :lg="primary.country && primary.country !== country_USA ? 4 : 6"
            :md="primary.country && primary.country !== country_USA ? 4 : 6"
            sm="6">
            <v-text-field
              outlined
              v-model="primary.city"
              label="City"
              :disabled="!makeChanges"
              :rules="[validateCity]"
            />
          </v-col>
          <v-col v-if="primary.country && primary.country !== country_USA" lg="8" md="8" sm="6" >
            <v-text-field
              outlined
              v-model="primary.international"
              label="International Area"
              :disabled="!makeChanges"
              :rules="[(v) => !!v || 'International Area is required']"
            />
          </v-col>
          <v-col
            :lg="primary.country && primary.country !== country_USA ? 4 : 6"
            :md="primary.country && primary.country !== country_USA ? 4 : 6"
            sm="6">
            <v-text-field
              outlined
              v-model="primary.zipPostal"
              label="Zip/Postal"
              :disabled="!makeChanges"
              :rules="[validateZip]"
            />
          </v-col>
        </v-row>
      </v-form>
      <v-checkbox
        v-model="makeChanges"
        :disabled="!isPrimaryValid"
        label="I need to make updates to my information"
      />
    </div>
    <div>
      <div class="form-sub-label">MEMBERSHIP TYPE</div>
      <div class="plans-container">
        <div class="plan-container" v-for="(plan, idx) in plans" :key="plan.membershipId">
          <v-card class="plan-card" hover v-on:click="selectPlan(idx, true)">
            <div class="d-flex flex-column">
              <h3>{{plan.title}}</h3>
              <p>{{plan.subtitle}}</p>
              <div class="d-flex flex-column align-start">
                <div v-for="(check, i) in plan.checkItems" :key="`${i}-check`" class="plan-item">
                  <font-awesome-icon icon="check" /> {{check}}
                </div>
                <div v-if="!!plan.items.length" class="mt-2" />
                <div v-for="(item, i) in plan.items" :key="`${i}-item`" class="plan-item">
                  {{item}}
                </div>
              </div>
            </div>
            <h1>{{plan.displayPrice}}</h1>
          </v-card>
          <v-checkbox
            v-model="plan.checked"
            v-on:click="selectPlan(idx)"
          />
        </div>
      </div>
    </div>
    <div v-if="!isJunior">
      <h2 v-if="isRenewal" class="additional-members-title">Additional Members</h2>
      <h2 v-else class="additional-members-title">Invite Additional Members</h2>
      <div v-if="addSecondary">
        <div class="form-container">
          <div class="form-label">Secondary Member</div>
          <v-form v-model="isSecondaryValid">
            <v-text-field
              v-if="!!secondary.memberId"
              outlined
              v-model="secondary.memberId"
              label="Member ID"
              :disabled="true"
            />
            <v-text-field
              v-if="!!secondary.memberId"
              outlined
              v-model="secondary.firstName"
              label="First Name"
              :rules="[(v) => !!v || 'First name is required']"
              :disabled="!!secondary.memberId"
            />
            <v-text-field
              v-if="!!secondary.memberId"
              outlined
              v-model="secondary.lastName"
              label="Last Name"
              :rules="[(v) => !!v || 'Last name is required']"
              :disabled="!!secondary.memberId"
            />
            <v-text-field
              outlined
              v-model="secondary.email"
              label="Email"
              :rules="[validateEmail, validateDualEmail]"
              :disabled="!!secondary.memberId"
            />
            <v-text-field
              v-if="!!secondary.memberId"
              outlined
              v-model="secondary.phone"
              label="Phone"
              :rules="[(v) => !!v || 'Phone is required']"
              :disabled="!!secondary.memberId"
            />
          </v-form>
          <div>
            <div class="form-sub-label">MEMBERSHIP TYPE</div>
            <v-radio-group disabled :value="true">
              <v-radio
                color="primary"
                :label="`${selectedPlan ? selectedPlan.title : ''} - ($${selectedPlan ? selectedPlan.price : ''})`"
                :value="true"
              />
            </v-radio-group>
          </div>
        </div>
      </div>
      <div>
        <div>
          <v-checkbox
            v-model="addJuniors"
            label="Add a Junior Member (under 18  years old)"
          />
        </div>
        <div v-if="addJuniors" class="form-container junior">
          <div v-for="(junior, idx) in juniors" :key="junior.key">
            <div class="form-label">
              <p>Junior Member</p>
              <a v-if="juniors.length > 1 && !junior.memberId" v-on:click="() => removeJunior(idx)">
                <font-awesome-icon icon="trash-alt" size="lg" />
              </a>
            </div>
            <v-form v-model="junior.isValid" :key="`${junior.key}-form`">
              <v-text-field
                v-if="!!junior.memberId"
                outlined
                v-model="junior.memberId"
                label="Member ID"
                :disabled="true"
              />
              <v-text-field
                v-if="!!junior.memberId"
                outlined
                v-model="junior.firstName"
                label="First Name"
                :disabled="!addJunior || !!junior.memberId"
                :rules="[(v) => !!v || 'First name is required']"
              />
              <v-text-field
                v-if="!!junior.memberId"
                outlined
                v-model="junior.lastName"
                label="Last Name"
                :disabled="!addJunior || !!junior.memberId"
                :rules="[(v) => !!v || 'Last name is required']"
              />
              <v-text-field
                outlined
                v-model="junior.email"
                label="Email"
                :disabled="!addJunior || !!junior.memberId"
                :rules="[validateEmail]"
              />
              <v-text-field
                v-if="!!junior.memberId"
                outlined
                v-model="junior.phone"
                label="Phone"
                :disabled="!addJunior || !!junior.memberId"
                :rules="[(v) => !!v || 'Phone is required']"
              />
              <DatepickerFormatted
                :date="junior.birthDate"
                :disabled="!addJunior || !!junior.memberId"
                label="Date of Birth"
                v-on:updateDates="updateJuniorBirthDate($event, idx)"
                :rules="[validateIsJunior]"
              />
            </v-form>
            <div v-if="idx === juniors.length - 1">
              <div class="form-sub-label">MEMBERSHIP TYPE</div>
              <v-radio-group disabled :value="true">
                <v-radio
                  color="primary"
                  label="Junior ($0)"
                  :value="true"
                />
              </v-radio-group>
            </div>
            <div v-if="idx === juniors.length - 1 && juniors.length < 5">
              <v-checkbox
                v-model="addAnotherJunior"
                label="Add an additional Junior Member (under 18  years old)"
                v-on:click="addJunior"
              />  
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="submit-container">
      <v-btn
        v-on:click="submit()"
        :dark="canSubmit()"
        large
        color="primary"
        :disabled="!canSubmit()"
      >Next</v-btn>
      <div />
      <div />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { v4 as uuidv4 } from 'uuid';

import CountryDropdown from '@/components/common/inputs/CountryDropdown.vue';
import StateDropdown from '@/components/common/inputs/StateDropdown.vue';
import DatepickerFormatted from '@/components/common/inputs/DatepickerFormatted.vue';
import UniqueEmailDialog from '@/components/common/UniqueEmailDialog.vue';
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';

import authenticationService from '@/services/authenticationService';
import memberService from "@/services/portal/memberService";

import { UserInfo } from "@/types/User";
import { MemberType } from "@/types/Member";
import { Membership } from '@/types/portal/Member';

import { isJunior } from '@/helpers/validate-junior-age';
import validateEmail from '@/helpers/validate-email';
import normalizeAddressRegion from '@/helpers/normalize-address-region';
import { Countries } from "@/constants";

// TODO: Once time allows debounce the email validation here
export default Vue.extend({
  components: { DatepickerFormatted, CountryDropdown, StateDropdown, UniqueEmailDialog, InternationalPhoneNumberInput },
  name: 'MemberForm',
  props: {
    isRenewal: { type: Boolean }
  },
  mounted: function() {
    const userInfo = this.$store.state.userInfo;
    if (!!userInfo) this.initialize();
  },
  data: function () {
    return {
      isProcessing: false,
      isJunior: false,

      showLoader: false,
      showNonUniqueEmail: false,
      
      isPrimaryValid: true,
      isSecondaryValid: true,

      makeChanges: false,
      addSecondary: false,
      addJuniors: false,
      addAnotherJunior: false,
      
      showResults: [] as any[],

      primary: {
        memberId: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        country: Countries.USA,
        address: "",
        international: "",
        city: "",
        stateProvince: "",
        zipPostal: "",
      },
      secondary: {
        memberId: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      },
      juniors: [
        {
          key: uuidv4(),
          isValid: false,
          memberId: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          birthDate: null,
        }
      ],
      plans: [] as Membership[],
      country_USA: Countries.USA,    
    };
  },
  watch: {
    isPrimaryValid: function(newValue) {
      if (!newValue && !this.makeChanges) {
        this.makeChanges = true;
        (this.$refs.primaryForm as any).validate();
      }
    },
    addJuniors: async function(newValue) {
      if (!newValue) {
        await Vue.nextTick();

        // Rerender the juniors after removing one of them - keep the vuelidate blank space away
        this.juniors = this.juniors.map(j => ({ ...j, key: uuidv4() }));
      }
    },
    userInfo: async function(newValue) {
      if (!!newValue) this.initialize();
    },
    selectedPlan: function (newValue) {
      if (!!newValue) {
        const plan: Membership = newValue;

        switch(plan.memberType) {
          case MemberType.Dual:
          case MemberType.LifetimeDual:
            this.isJunior = false;
            this.addSecondary = true;  
            break;
          case MemberType.Junior:
            this.isJunior = true;
            this.addSecondary = false;
            break;
          default:
            this.isJunior = false;
            this.addSecondary = false;

            // Reset when selecting non-dual member type
            if (!this.secondary.memberId) {
              this.secondary = {
                memberId: "",
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
              };
            }
            break;
        }
      }
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    selectedPlan(): Membership | null {
      const plan = this.plans.find((p) => p.checked);
      return plan ?? null;
    },
  },
  methods: {
    // INITIALIZATION
    initialize: async function () {
      this.isProcessing = true;

      const {
        memberId,
        firstName,
        lastName,
        email,
        phone,
        country,
        stateProvince,
        address,
        international,
        city,
        zipPostal,
        isJuniorHandler
      } = this.userInfo;

      const primary = {
        memberId,
        firstName,
        lastName,
        email,
        phone,
        country: country ? country.toUpperCase() : Countries.USA,
        address,
        international,
        city,
        stateProvince,
        zipPostal,
      };

      const region = normalizeAddressRegion(primary.country, stateProvince, international);
      primary.stateProvince = region.state;
      primary.international = region.international;

      this.primary = primary;
      this.isJunior = isJuniorHandler;

      await this.getPlans();
      await this.getAuxMembers();

      this.isProcessing = false;
    },
    getPlans: async function () {
      const isJunior = this.isJunior;
      const memberId = (this.primary as any).memberId;

      const fees = await memberService.getMemberFees();

      if (!!fees) {
        this.plans = fees.memberships.map(p => ({
          ...p,
          key: uuidv4(),
          checked: !!memberId
            ? p.memberType == parseInt(memberId[0])
            : isJunior
        })) as any;

        this.$store.commit('changeRegisterRenewMembershipInfo',
        {
          aussieTimeFees: {
            surfacePostageCanada: fees.surfacePostageCanada,
            surfacePostageOther: fees.surfacePostageOther,
            firstClassUsa: fees.firstClassUsa,
            firstClassCanada: fees.firstClassCanada,
            firstClassOther: fees.firstClassOther,
          }
        });
      }
    },
    getAuxMembers: async function() {
      if (this.isRenewal) {
        try {
          const axm = await memberService.getAuxiliaryMembers();

          if (!!axm.secondary) {
            this.secondary = { ...axm.secondary };
          }

          if (!!axm.juniors && !!axm.juniors.length) {
            this.addJuniors = true;
            this.juniors = axm.juniors.map((j: any) => ({
              ...j,
              key: uuidv4(),
              isValid: true,
            }));
          }
        } catch (error) {
          this.$handleError(error);
        }
      }
    },
    // VALIDATION
    validateEmail(v: string) {
      return (
        validateEmail(v) ||
        "E-mail must be valid"
      );
    },
    validateDualEmail(v: string) {
      return this.secondary.email !== this.primary.email || "E-mail can't be the same as the primary E-mail";
    },
    validateIsJunior(v: string) {
      return (
        (!!v && isJunior(new Date(v)))
        || 'Must be under 18 years old'
      )
    },
    validateCity(v: string) {
      return this.primary.country === Countries.USA
        ? !!v || 'City is required'
        : true
    },
    validateZip(v: string) {
      return this.primary.country === Countries.USA
        ? !!v || 'Zip/Postal is required'
        : true
    },
    canSubmit() {
      if (this.isJunior) {
        return this.isPrimaryValid && this.plans.some((p: any) => p?.checked === true) as any;
      } else if (this.addSecondary) {
        return this.isPrimaryValid
          && this.plans.some((p: any) => p?.checked === true) as any
          && (this.addSecondary && !this.secondary.memberId ? this.isSecondaryValid : true)
          && (this.addJuniors ? !(this.juniors.some((j) => !(j as any).memberId && (!(j as any).isValid || !(j as any).birthDate))) : true);  
      } else {
        return this.isPrimaryValid
          && this.plans.some((p: any) => p?.checked === true) as any
          && (this.addJuniors ? !(this.juniors.some((j) => !(j as any).memberId && (!(j as any).isValid || !(j as any).birthDate))) : true);  
      }
    },
    checkEmail: async function(newEmail?: string) {
      if (this.primary.email === this.userInfo.email) return true;

      if (!!newEmail) this.primary.email = newEmail;

      const response = await authenticationService.uniqueEmail(this.primary.email);

      this.showNonUniqueEmail = !response.unique;

      return response.unique;
    },
    // MODIFICATION
    updateJuniorBirthDate: function (val: any, juniorIndex: number) {
      this.juniors[juniorIndex].birthDate = val.date;
      //workaround to force rerendering after setting up formatted date
      this.$set(this, "showResults", this.showResults);
    },
    updateCountry: async function (v: string) {
      this.primary.country = v;
      this.normalizePrimaryAddressRegion();

      await Vue.nextTick();

      (this.$refs.primaryForm as any).validate();
    },
    selectPlan: async function (planToSelect: number, update: boolean = false) {
      if (this.plans.length <= 1) return;

      this.plans = this.plans.map((p: any, i: number) => ({
        ...p,
        checked: i == planToSelect
          ? update
            ? !p?.checked
            : p?.checked
          : false
      }));
    },
    addJunior() {
      this.addAnotherJunior = false;
      this.juniors.push({
        key: uuidv4(),
        isValid: false,
        memberId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        birthDate: null,
      });
    },
    removeJunior: async function (index: number) {
      this.juniors = this.juniors.filter((j, i) => i !== index);

      await Vue.nextTick();

      // Rerender the juniors after removing one of them - keep the vuelidate blank space away
      this.juniors = this.juniors.map(j => ({ ...j, key: uuidv4() }));
    },
    submit: async function() {
      this.normalizePrimaryAddressRegion();
      this.isProcessing = true;

      const uniqueEmail = await this.checkEmail();

      if (uniqueEmail && this.canSubmit()) {
        const selectedPlan = this.selectedPlan;

        this.isProcessing = false;
        this.$store.commit('changeRegisterRenewMembershipInfo',
        {
          page: selectedPlan!.memberType === MemberType.Service ? 3 : 2,
          isRenew: this.isRenewal,
          isAussieRenewal: false,
          subscribe: false,
          firstClass: false,
          plan: selectedPlan,
          primary: {
            ...this.primary,
          },
          secondary: this.addSecondary && !this.secondary.memberId ? this.secondary : null,
          juniors: this.addJuniors ? this.juniors.filter((j) => !j.memberId) : [],
        });
      }

      this.isProcessing = false;
    },
    normalizePrimaryAddressRegion: function () {
      const region = normalizeAddressRegion(
        this.primary.country,
        this.primary.stateProvince,
        this.primary.international
      );
      this.primary.stateProvince = region.state;
      this.primary.international = region.international;
    },
  },
});
</script>
